import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import GoogleMapComponent from '../../../components/GoogleMapComponent';
import { generateArabicDescription } from '../../../services/aiPropertyDescriptionService';

const PropertyDetailsPopup = ({ property, onClose, userRole }) => {
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [aiDescription, setAiDescription] = useState(null);
  const [showAiDescription, setShowAiDescription] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  if (!property) return null;

  const formatCurrency = (amount, currency) => {
    const formatted = new Intl.NumberFormat('ar-EG')?.format(amount);
    return `${formatted} ${currency}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير متوفر';
    const date = new Date(dateString);
    if (isNaN(date?.getTime())) return 'غير متوفر';
    return new Intl.DateTimeFormat('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })?.format(date);
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.2 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.15 }
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.95,
      y: 20
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { 
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: { 
        duration: 0.2
      }
    }
  };

  const handleGenerateArabicDescription = async () => {
    setIsGeneratingDescription(true);
    try {
      const result = await generateArabicDescription(property, 'professional');
      setAiDescription(result);
      setShowAiDescription(true);
    } catch (error) {
      alert(error?.message || 'فشل في توليد الوصف');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={overlayVariants}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/75 backdrop-blur-md"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          className="relative bg-gradient-to-br from-[#1a1a1a] via-[#1f1f1f] to-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden border border-primary/20"
          variants={modalVariants}
          onClick={(e) => e?.stopPropagation()}
          style={{
            boxShadow: '0 25px 50px -12px rgba(184, 134, 11, 0.25), 0 0 0 1px rgba(184, 134, 11, 0.1)'
          }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10 border-b border-primary/20 px-4 py-3 sm:px-6 sm:py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border border-primary/30 shadow-lg flex-shrink-0">
                  <Image
                    src={property?.image}
                    alt={property?.imageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-xl font-heading font-bold text-white mb-1.5 break-words leading-tight">
                    {property?.address}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-md bg-primary/15 text-primary border border-primary/30 text-[10px] sm:text-xs font-medium">
                      {property?.propertyId}
                    </span>
                    <span className="px-2 py-0.5 rounded-md bg-accent/15 text-accent border border-accent/30 text-[10px] sm:text-xs font-medium">
                      {property?.type}
                    </span>
                    <span className={`px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-medium ${
                      property?.status === 'متاح' ? 'bg-success/15 text-success border border-success/30' :
                      property?.status === 'محجوز'? 'bg-warning/15 text-warning border border-warning/30' : 'bg-primary/15 text-primary border border-primary/30'
                    }`}>
                      {property?.status}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={onClose}
                className="text-white hover:bg-white/10 flex-shrink-0 h-8 w-8"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border/30 bg-muted/5 px-4 sm:px-6">
            <div className="flex gap-1 sm:gap-2">
              <button
                onClick={() => setActiveTab('details')}
                className={`px-3 sm:px-4 py-2 text-[11px] sm:text-xs font-medium transition-all border-b-2 ${
                  activeTab === 'details' ?'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground'
                }`}
              >
                التفاصيل
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`px-3 sm:px-4 py-2 text-[11px] sm:text-xs font-medium transition-all border-b-2 ${
                  activeTab === 'pricing' ?'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground'
                }`}
              >
                التسعير
              </button>
              <button
                onClick={() => setActiveTab('location')}
                className={`px-3 sm:px-4 py-2 text-[11px] sm:text-xs font-medium transition-all border-b-2 ${
                  activeTab === 'location' ?'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground'
                }`}
              >
                الموقع
              </button>
              <button
                onClick={() => setActiveTab('ai')}
                className={`px-3 sm:px-4 py-2 text-[11px] sm:text-xs font-medium transition-all border-b-2 ${
                  activeTab === 'ai' ?'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground'
                }`}
              >
                وصف ذكي
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(92vh-180px)] sm:max-h-[calc(92vh-200px)]">
            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {/* Property Info Card */}
                <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg p-3 sm:p-4 border border-primary/10">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Icon name="Home" size={16} className="text-primary flex-shrink-0" />
                    <h3 className="text-xs sm:text-sm font-heading font-semibold text-white">معلومات العقار</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] sm:text-xs text-gray-400">النوع</span>
                      <span className="text-[11px] sm:text-xs text-white font-medium">{property?.type}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] sm:text-xs text-gray-400">المساحة</span>
                      <span className="text-[11px] sm:text-xs text-white font-medium">{property?.area} م²</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] sm:text-xs text-gray-400">الحالة</span>
                      <span className="text-[11px] sm:text-xs text-white font-medium">{property?.status}</span>
                    </div>
                  </div>
                </div>

                {/* Owner Info Card */}
                <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg p-3 sm:p-4 border border-primary/10">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Icon name="User" size={16} className="text-primary flex-shrink-0" />
                    <h3 className="text-xs sm:text-sm font-heading font-semibold text-white">المالك</h3>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">الاسم</p>
                      <p className="text-[11px] sm:text-xs text-white font-medium break-words">{property?.owner}</p>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">رقم الهاتف</p>
                      <a 
                        href={`tel:${property?.ownerPhone}`}
                        className="text-[11px] sm:text-xs text-primary hover:text-primary/80 font-data break-words transition-colors inline-flex items-center gap-1" 
                        dir="ltr"
                      >
                        <Icon name="Phone" size={12} />
                        {property?.ownerPhone}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Location Info Card */}
                <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg p-3 sm:p-4 border border-primary/10">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Icon name="MapPin" size={16} className="text-primary flex-shrink-0" />
                    <h3 className="text-xs sm:text-sm font-heading font-semibold text-white">الموقع</h3>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">المنطقة</p>
                      <p className="text-[11px] sm:text-xs text-white font-medium break-words">{property?.district}</p>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">المدينة</p>
                      <p className="text-[11px] sm:text-xs text-white font-medium break-words">{property?.city}</p>
                    </div>
                  </div>
                </div>

                {/* Status & Updates Card */}
                <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg p-3 sm:p-4 border border-primary/10 md:col-span-2 lg:col-span-3">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Icon name="Activity" size={16} className="text-primary flex-shrink-0" />
                    <h3 className="text-xs sm:text-sm font-heading font-semibold text-white">الحالة والتحديثات</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">آخر تحديث</p>
                      <p className="text-[11px] sm:text-xs text-white font-medium font-data" dir="ltr">{formatDate(property?.lastUpdated)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">تاريخ الإضافة</p>
                      <p className="text-[11px] sm:text-xs text-white font-medium font-data" dir="ltr">{formatDate(property?.dateAdded)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-0.5">عدد المشاهدات</p>
                      <p className="text-[11px] sm:text-xs text-white font-medium font-data" dir="ltr">{property?.views || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pricing Tab */}
            {activeTab === 'pricing' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-4 sm:p-5 border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Tag" size={18} className="text-primary" />
                    <h3 className="text-sm font-heading font-semibold text-white">السعر المطلوب</h3>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-primary font-data" dir="ltr">
                    {formatCurrency(property?.askingPrice, property?.currency)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-lg p-4 sm:p-5 border border-success/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Banknote" size={18} className="text-success" />
                    <h3 className="text-sm font-heading font-semibold text-white">سعر البيع</h3>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-success font-data" dir="ltr">
                    {formatCurrency(property?.salePrice, property?.currency)}
                  </p>
                  {property?.askingPrice > property?.salePrice && (
                    <p className="text-xs text-gray-400 mt-2">
                      خصم {Math.round(((property?.askingPrice - property?.salePrice) / property?.askingPrice) * 100)}%
                    </p>
                  )}
                </div>

                {userRole === 'admin' && property?.profit && (
                  <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-lg p-4 sm:p-5 border border-accent/20 sm:col-span-2">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="TrendingUp" size={18} className="text-accent" />
                      <h3 className="text-sm font-heading font-semibold text-white">الربح المتوقع</h3>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-accent font-data" dir="ltr">
                      {formatCurrency(property?.profit, property?.currency)}
                    </p>
                  </div>
                )}

                <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg p-4 border border-primary/10 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="MessageCircle" size={16} className="text-primary" />
                      <span className="text-xs sm:text-sm text-white font-medium">قابل للتفاوض</span>
                    </div>
                    <span className={`px-3 py-1 rounded-md text-xs font-medium ${
                      property?.negotiable 
                        ? 'bg-success/15 text-success border border-success/30' :'bg-error/15 text-error border border-error/30'
                    }`}>
                      {property?.negotiable ? 'نعم' : 'لا'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Location Tab */}
            {activeTab === 'location' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg p-4 border border-primary/10">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-1">العنوان الكامل</p>
                      <p className="text-xs sm:text-sm text-white font-medium break-words">{property?.address}</p>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-1">المنطقة</p>
                      <p className="text-xs sm:text-sm text-white font-medium break-words">{property?.district}</p>
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-1">المدينة</p>
                      <p className="text-xs sm:text-sm text-white font-medium break-words">{property?.city}</p>
                    </div>
                  </div>
                </div>

                {property?.lat && property?.lng && (
                  <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg p-4 border border-primary/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon name="Map" size={16} className="text-primary" />
                        <h3 className="text-xs sm:text-sm font-heading font-semibold text-white">موقع العقار على الخريطة</h3>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ExternalLink"
                        onClick={() => window.open(`https://www.google.com/maps?q=${property?.lat},${property?.lng}`, '_blank')}
                        className="text-primary border-primary/30 hover:bg-primary/10 text-[10px] sm:text-xs h-7"
                      >
                        فتح في خرائط جوجل
                      </Button>
                    </div>
                    <div className="rounded-lg overflow-hidden border border-primary/20">
                      <GoogleMapComponent
                        latitude={property?.lat}
                        longitude={property?.lng}
                        zoom={15}
                        height="400px"
                        draggable={false}
                        onLocationSelect={() => {}}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Description Tab */}
            {activeTab === 'ai' && (
              <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg p-4 sm:p-5 border border-primary/10">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Sparkles" size={18} className="text-primary" />
                    <h3 className="text-sm sm:text-base font-heading font-semibold text-white">وصف ذكي بالعربية</h3>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateArabicDescription}
                    disabled={isGeneratingDescription}
                    className="text-primary border-primary/30 hover:bg-primary/10 text-xs"
                  >
                    {isGeneratingDescription ? (
                      <>
                        <Icon name="Loader2" size={14} className="animate-spin ml-1" />
                        جاري التوليد...
                      </>
                    ) : (
                      'توليد وصف'
                    )}
                  </Button>
                </div>
                {showAiDescription && aiDescription ? (
                  <div className="space-y-4">
                    <div className="bg-primary/5 rounded-lg p-3 border border-primary/20">
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-1.5 font-semibold">عنوان تسويقي:</p>
                      <p className="text-xs sm:text-sm text-white font-medium break-words leading-relaxed">{aiDescription?.marketingTitle}</p>
                    </div>
                    <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700/30">
                      <p className="text-[10px] sm:text-xs text-gray-400 mb-1.5 font-semibold">وصف متوسط:</p>
                      <p className="text-[11px] sm:text-xs text-gray-300 leading-relaxed break-words">{aiDescription?.mediumDescription}</p>
                    </div>
                    {aiDescription?.highlights?.length > 0 && (
                      <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700/30">
                        <p className="text-[10px] sm:text-xs text-gray-400 mb-2 font-semibold">النقاط البارزة:</p>
                        <ul className="space-y-1.5">
                          {aiDescription?.highlights?.map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Icon name="Check" size={14} className="text-primary flex-shrink-0 mt-0.5" />
                              <span className="text-[11px] sm:text-xs text-gray-300 break-words">{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="Sparkles" size={40} className="text-primary/30 mx-auto mb-3" />
                    <p className="text-xs sm:text-sm text-gray-400">انقر على "توليد وصف" لإنشاء وصف احترافي بالذكاء الاصطناعي</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gradient-to-r from-gray-900/50 via-gray-800/50 to-gray-900/50 border-t border-primary/20 px-4 py-3 sm:px-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-[10px] sm:text-xs text-gray-400">
                <Icon name="Info" size={14} className="text-primary" />
                <span>معرف العقار: <span className="text-white font-data" dir="ltr">{property?.propertyId}</span></span>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                onClick={onClose}
                className="text-xs"
              >
                إغلاق
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PropertyDetailsPopup;